import React, { useState } from "react";
import { motion } from "framer-motion";
import { Monogram } from "./Monogram";

export const Envelope = ({ data, onOpen }) => {
  const c = data.colors;
  const scale = (data.fontScale || 1) * ((data.sizes && data.sizes.monogram) || 1);
  const [opening, setOpening] = useState(false);

  const handleOpen = () => {
    if (opening) return;
    setOpening(true);
    // let the flap fold + letter rise before revealing the invitation
    setTimeout(() => onOpen(), 1500);
  };

  return (
    <motion.div
      key="envelope"
      className="absolute inset-0 z-40 flex items-center justify-center overflow-hidden grain-overlay"
      style={{ background: c.bg, perspective: 1600 }}
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7, ease: "easeInOut" }}
    >
      {data.bgImageEnvelope && (
        <div
          className="absolute inset-0"
          style={{ backgroundImage: `url(${data.bgImageEnvelope})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
      )}
      {data.bgImageEnvelope && <div className="absolute inset-0" style={{ background: `${c.bg}b0` }} />}

      {/* Envelope body bottom pocket */}
      <div
        className="absolute bottom-0 left-0 w-full"
        style={{
          height: "48%",
          background: `linear-gradient(20deg, #ffffff 0%, ${c.bg} 100%)`,
          clipPath: "polygon(50% 0, 0 100%, 100% 100%)",
          zIndex: 6,
        }}
      />

      {/* The letter that rises out of the envelope */}
      <motion.button
        data-testid="tap-to-open-btn"
        onClick={handleOpen}
        className="relative z-20 flex flex-col items-center gap-10 focus:outline-none"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={
          opening
            ? { y: -140, scale: 1.08, opacity: [1, 1, 0] }
            : { y: 0, scale: 1, opacity: 1 }
        }
        transition={
          opening
            ? { duration: 1.4, ease: "easeInOut", times: [0, 0.7, 1] }
            : { duration: 0.8, ease: "easeOut" }
        }
        whileTap={opening ? {} : { scale: 0.96 }}
      >
        <motion.div
          className="relative flex items-center justify-center"
          style={data.logoImage ? {} : {
            width: 168,
            height: 168,
            borderRadius: "9999px",
            background: "radial-gradient(circle at 35% 30%, #ffffff, #efe9e1)",
            boxShadow: "0 12px 40px rgba(92,22,46,0.12), inset 0 2px 6px rgba(255,255,255,0.9)",
            border: "1px solid rgba(212,175,55,0.3)",
          }}
          animate={opening ? { y: 0 } : { y: [0, -6, 0] }}
          transition={opening ? { duration: 0.4 } : { duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <Monogram data={data} size={data.logoImage ? 120 : 62 * scale} />
        </motion.div>

        <motion.span
          className="font-body tracking-luxe text-sm"
          style={{ color: c.secondary }}
          animate={opening ? { opacity: 0 } : { opacity: [0.4, 1, 0.4] }}
          transition={opening ? { duration: 0.3 } : { duration: 2.4, repeat: Infinity }}
        >
          {data.tapToOpenText}
        </motion.span>
      </motion.button>

      {/* Top flap — folds open on tap */}
      <motion.div
        className="absolute top-0 left-0 w-full"
        style={{
          height: "48%",
          background: `linear-gradient(160deg, ${c.bg} 0%, #ffffff 100%)`,
          clipPath: "polygon(0 0, 100% 0, 50% 100%)",
          boxShadow: "0 20px 40px rgba(0,0,0,0.05)",
          transformOrigin: "top center",
          transformStyle: "preserve-3d",
          zIndex: opening ? 7 : 10,
        }}
        initial={{ rotateX: 0 }}
        animate={{ rotateX: opening ? -175 : 0 }}
        transition={{ duration: 1, ease: [0.4, 0, 0.2, 1] }}
      />

      {!opening && (data.customBlocks || []).filter((b) => b.position === "envelope").length > 0 && (
        <div className="absolute z-[9] bottom-8 left-0 right-0 flex flex-col items-center gap-3 px-8" data-testid="envelope-blocks">
          {(data.customBlocks || []).filter((b) => b.position === "envelope").map((b) => (
            <div key={b.id} className="w-full max-w-[280px] text-center" style={{ marginTop: b.offsetY || 0 }}>
              {b.type === "image" && b.imageUrl && (
                <img src={b.imageUrl} alt={b.heading || "photo"} className="w-full rounded-2xl shadow-lg object-cover" style={{ maxHeight: 220 }} />
              )}
              {b.heading && <p dir="auto" className="font-display italic mt-2" style={{ fontSize: 16, color: c.text }}>{b.heading}</p>}
              {b.type === "text" && b.body && <p dir="auto" className="font-body mt-1" style={{ fontSize: 13, color: c.text }}>{b.body}</p>}
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};
